import {ID} from '../../../types/UtilityTypes'
import {filter, map, mapTo, mergeMap} from 'rxjs/operators'
import {isActionOf} from 'typesafe-actions'
import {taskActions} from './task-actions'
import {
    childrenSelector,
    flowSelectors,
    mapSelector,
    parentsSelector,
    taskSelector,
    userTasksSelector
} from '../../../utils/selectors'
import {concat, EMPTY, forkJoin, from, of} from 'rxjs'
import {EMPTY_TASK, ServerTask, Task, UpdateTaskRequest} from '../../../responses/tasks'
import {diffAndPrepare, diffEntityArray, entityRemove, eqEntity, prepareForServer} from '../../../utils/entity-utils'
import {unboxRecord} from '../../../utils/utils'
import {User} from '../../../responses/users'
import * as array from 'fp-ts/es6/Array'
import {apiRequests} from '../../../http/config'
import {infoToast, toastFailedResponse} from '../../../components/controls/Toaster/Toaster'
import {combineEpics} from 'redux-observable'
import {objectActions} from '../object/object-actions'
import {matchPath} from 'react-router'
import {urlWithParams} from '../../../http/utils'
import {ROUTES} from '../../../http/routes'
import {push} from 'connected-react-router'
import {TaskStatus} from '../../../constants/enums'
import {RootEpic} from '../../root'
import {navigationActions} from '../../global/navigation'
import * as A from 'fp-ts/Array'
import {contramap, ordNumber} from 'fp-ts/Ord'
import {flow, pipe} from 'fp-ts/function'
import {TaskEditState} from './task-reducer'

const get: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.get.request)),
    mergeMap(({payload}) => apiRequests.task.get({params: {taskId: payload}, query: {with_branch: 1}})),
    map(({response}) => response.success ?
        taskActions.get.success(response.row) :
        taskActions.get.failure(toastFailedResponse(response, 'Ошибка загрузки задачи')),
    ),
)

const allLinesWithGet: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.get.success)),
    map(action => taskActions.parents.request({objectId: action.payload.project_work_object_id}))
)

const parents: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.parents.request)),
    mergeMap(action => apiRequests.project.tasks.list({
        params: {objectId: action.payload.objectId},
        query: {parents: true, ...action.payload.query},
    }).pipe(
        map(res => {
            if (res.response.success) {
                return taskActions.parents.success({
                    objectId: action.payload.objectId,
                    tasks: res.response.rows,
                })
            } else {
                toastFailedResponse(res.response, 'Ошибка загрузки списка задач')
                return taskActions.parents.failure({
                    objectId: action.payload.objectId,
                })
            }
        })
    ))
)

const requested: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.requested.request)),
    mergeMap(() => apiRequests.task.list({
        query: {tasks_status: TaskStatus.REQUESTED},
    })),
    map(res => res.response.success ?
        taskActions.requested.success(res.response.rows) :
        taskActions.requested.failure(toastFailedResponse(res.response, 'Ошибка получения запросов на создание задач'))
    )
)
const create: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.create.request)),
    mergeMap(({payload}) => apiRequests.project.tasks.add({
        data: {
            ...prepareForServer(payload.task),
            parent_id: payload.parentId,
            comment: unboxRecord({
                text: payload.task.comment?.text,
                files: diffEntityArray([], payload.task.comment?.files),
            })
        },
        params: {objectId: payload.objectId}
    })),
    map(({response}) => response.success ?
        taskActions.create.success(response.row, 'user') :
        taskActions.create.failure(toastFailedResponse(response, 'Не удалось добавить задачу')),
    ),
)
const update: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(taskActions.update.request)),
    mergeMap(({payload}) => {
        const taskId = payload.id
        const task = taskSelector(taskId)(state$.value)

        if (!task) {
            return EMPTY
        }

        const data: UpdateTaskRequest = {
            ...diffAndPrepare(task, payload),
            comment: unboxRecord({
                text: payload.comment?.text,
                files: diffEntityArray([], payload.comment?.files),
            }),
        }
        const usersDiff: User[] = !payload.users ? [] :
            array.difference<User>(eqEntity)(task.users || [], payload.users)

        const requests = usersDiff.map(
            ({id}) => apiRequests.task.userDelete({params: {taskId, userId: id}})
        )
        forkJoin(requests).subscribe(res => res.forEach(
            res => !res.response.success && toastFailedResponse(res.response, 'Не удалось обновить участников')
        ))
        return apiRequests.task.update({data, params: {taskId}})
    }),
    map(res => res.response.success ?
        taskActions.update.success(res.response.row, 'user') :
        taskActions.update.failure(toastFailedResponse(res.response, 'Ошибка обновления задачи'))
    )
)
const remove: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(taskActions.remove.request)),
    mergeMap(() => TaskEditState.match({
        delete: ({task}) => apiRequests.task.delete({params: {taskId: task.id}}).pipe(
            mergeMap((res) => res.response.success ?
                from([
                    taskActions.remove.success(res.response.row),
                    taskActions.cancelEdit(),
                ]) :
                of(taskActions.remove.failure(toastFailedResponse(res.response, 'Не удалось удалить задачу')))
            )
        )
    }, () => EMPTY)(state$.value.data.task.editState)),
)
const deleteTask: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(taskActions.remove.success)),
    map(action => taskActions._delete({
        task: action.payload,
        children: childrenSelector(action.payload)(state$.value)
    })),
)

const user: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.user.request)),
    mergeMap(action => apiRequests.users.tasks({
        params: {id: action.payload},
    }).pipe(
        map(res => res.response.success ?
            taskActions.user.success({userId: action.payload, tasks: res.response.rows}) :
            taskActions.user.failure()
        )
    )),
)

const redirectOnError: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.get.failure)),
    mapTo(navigationActions.goBack(ROUTES.TASKS))
)

const redirectOnRemove: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.remove.success)),
    mergeMap(action => {
        const onPage = matchPath(location.pathname, {
            path: urlWithParams({
                taskId: action.payload
            })(ROUTES.TASK)
        })
        if (onPage) {
            infoToast('Задача удалена. Возврат на страницу задач')
            return of(push(ROUTES.TASKS))
        }
        return EMPTY
    }),
)

const objectRemove: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(objectActions.remove.success)),
    mergeMap(action => {
        const taskIds: ID[] = parentsSelector(action.payload)(state$.value)
        const ids = taskIds.flatMap(parentId => [parentId, ...childrenSelector(parentId)(state$.value)])
        return from(ids.map(taskActions.remove.success))
    }),
)

const comments: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.comments.request)),
    mergeMap(action => apiRequests.task.comments({
        query: {resulted: action.payload.resulted},
        params: {taskId: action.payload.taskId},
    }).pipe(
        map(res => res.response.success ?
            taskActions.comments.success({
                ...action.payload,
                comments: res.response.rows,
            }) :
            taskActions.comments.failure(toastFailedResponse(res.response, 'Не удалось получить комментарии к задаче'))
        )
    )),
)

const fullUserTasksSelector = flowSelectors(userTasksSelector, mapSelector(taskSelector))
const prepareTasks = flow(
    A.sort(contramap((task: Task) => task.priority_id)(ordNumber)),
    A.reverse,
)

const refresh: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(taskActions.update.success)),
    mergeMap(action => {
        const oldTask = taskSelector(action.payload.id)(state$.value) ?? EMPTY_TASK
        const newTask = action.payload
        // console.log(oldTask, newTask)
        if (oldTask?.target_user != newTask.target_user) {
            console.log('here')
            return concat(
                oldTask?.target_user ? of(taskActions.user.success({
                    userId: oldTask.target_user.id,
                    tasks: pipe(
                        state$.value,
                        fullUserTasksSelector(oldTask.target_user?.id),
                        entityRemove<Task>(oldTask),
                        prepareTasks,
                    ) as ServerTask[],
                })) : EMPTY,
                newTask?.target_user ? of(taskActions.user.success({
                    userId: newTask.target_user.id,
                    tasks: pipe(
                        state$.value,
                        fullUserTasksSelector(newTask.target_user.id),
                        A.cons<Task>(newTask),
                        prepareTasks,
                    ) as ServerTask[],
                })) : EMPTY,
            )
        }
        return EMPTY
    })
)

function downloadFile(filename: any, task_id: any) { // eslint-disable-line
    console.log(filename)
    const element = document.createElement('a')
    element.setAttribute('href', filename)
    element.setAttribute('download', `waybill_${task_id}.pdf`)
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
}

const waybill: RootEpic = action$ => action$.pipe(
    filter(isActionOf(taskActions.waybill.request)),
    mergeMap(action => apiRequests.task.waybill({
        params: {id: action.payload.id},
    }).pipe(
        map(res => {
                /*res.response.success ?
                    taskActions.waybill.success({row: action.payload, tasks: res.response.rows}) :
                    taskActions.waybill.failure()*/
                if (res.response.success) {
                    const result = taskActions.waybill.success({
                        row: action.payload,
                        file: res.response.row.filename,
                        task_id: res.response.row.task_id
                    })
                    console.log(result)
                    downloadFile(result.payload.file, result.payload.task_id)
                    return result
                }
                return taskActions.waybill.failure()
            }
        )
    )),
)
export const taskEpic = combineEpics(
    get, parents, requested, create, update, remove, deleteTask, user, redirectOnError, redirectOnRemove,
    objectRemove, comments, allLinesWithGet, refresh, waybill
)